/**
 * Learned-rules admin API client ( section E + F).
 *
 * Talks to the operator-only `/admin/learned-rules*` endpoints exposed by
 * `backend/app/routers/learned_rules.py` and the existing
 * `/admin/flags` endpoint for the hardcoded rubric. Used by the Admin
 * Rubric tab + Learned Rules sub-section.
 */
import apiClient from './client';

/* ---------- Hardcoded rubric flags ---------- */

export interface RubricFlag {
  id: string;
  category: number;
  flag_code: string;
  description: string;
  severity: 'AUTO_ESCALATE' | 'HIGH' | 'MEDIUM' | 'LOW';
  points: number;
  is_active: boolean;
}

export async function listRubricFlags(includeInactive = true): Promise<RubricFlag[]> {
  const { data } = await apiClient.get<{ items: RubricFlag[] }>('/admin/flags', {
    params: includeInactive ? { include_inactive: true } : {},
  });
  return data.items;
}

export async function patchRubricFlag(
  flagId: string,
  patch: { points?: number; severity?: RubricFlag['severity']; is_active?: boolean },
): Promise<RubricFlag> {
  const { data } = await apiClient.patch<RubricFlag>(`/admin/flags/${flagId}`, patch);
  return data;
}

/* ---------- Learned rules ---------- */

export type VersionStatus = 'draft' | 'active' | 'retired';
export type TargetData = 'documents' | 'public_data' | 'both';
export type Severity = 'auto_escalate' | 'high' | 'medium' | 'low';

export interface LearnedRuleVersion {
  id: string;
  rule_id: string;
  rule_code: string;
  rule_name: string;
  rule_enabled: boolean;
  category: number;
  version_no: number;
  detection_prompt: string;
  target_data: TargetData;
  severity: Severity;
  points: number;
  evidence_fields: string[];
  negative_examples: string[];
  source_deficiency: string | null;
  source_case_id: string | null;
  rationale: string | null;
  status: VersionStatus;
  created_at: string | null;
  retired_at: string | null;
}

export interface LearnedRule {
  id: string;
  code: string;
  name: string;
  category: number;
  enabled: boolean;
  created_at: string | null;
  updated_at: string | null;
  versions?: LearnedRuleVersion[];
  active_version?: LearnedRuleVersion | null;
}

export interface GeneratedDraft {
  code: string;
  name: string;
  category: number;
  target_data: TargetData;
  detection_prompt: string;
  severity: Severity;
  points: number;
  evidence_fields: string[];
  negative_examples: string[];
  rationale: string;
}

/* ---------- Endpoints ---------- */

export async function listLearnedRules(): Promise<LearnedRule[]> {
  const { data } = await apiClient.get<{ items: LearnedRule[] }>('/admin/learned-rules');
  return data.items;
}

export async function generateLearnedRuleDraft(
  deficiencyDescription: string,
  exampleCaseId: string,
  targetData: TargetData = 'both',
): Promise<GeneratedDraft> {
  const { data } = await apiClient.post<{ draft: GeneratedDraft }>(
    '/admin/learned-rules/generate',
    {
      deficiency_description: deficiencyDescription,
      example_case_id: exampleCaseId,
      target_data: targetData,
    },
  );
  return data.draft;
}

export interface CreateLearnedRuleInput extends Partial<GeneratedDraft> {
  code: string;
  name: string;
  category: number;
  target_data: TargetData;
  detection_prompt: string;
  severity: Severity;
  points: number;
  source_deficiency?: string;
  source_case_id?: string | null;
}

export async function createLearnedRule(input: CreateLearnedRuleInput): Promise<LearnedRule> {
  const { data } = await apiClient.post<LearnedRule>('/admin/learned-rules', input);
  return data;
}

export async function createDraftVersion(
  ruleId: string,
  input: {
    detection_prompt: string;
    target_data: TargetData;
    severity: Severity;
    points: number;
    evidence_fields?: string[];
    negative_examples?: string[];
    source_deficiency?: string;
    source_case_id?: string | null;
    rationale?: string;
  },
): Promise<LearnedRule> {
  const { data } = await apiClient.post<LearnedRule>(
    `/admin/learned-rules/${ruleId}/versions`,
    input,
  );
  return data;
}

export async function editDraftVersion(
  ruleId: string,
  versionNo: number,
  patch: {
    detection_prompt?: string;
    target_data?: TargetData;
    severity?: Severity;
    points?: number;
    evidence_fields?: string[];
    negative_examples?: string[];
    rationale?: string;
  },
): Promise<LearnedRuleVersion> {
  const { data } = await apiClient.patch<LearnedRuleVersion>(
    `/admin/learned-rules/${ruleId}/versions/${versionNo}`,
    patch,
  );
  return data;
}

export async function publishDraftVersion(
  ruleId: string,
  versionNo: number,
): Promise<LearnedRule> {
  const { data } = await apiClient.post<LearnedRule>(
    `/admin/learned-rules/${ruleId}/versions/${versionNo}/publish`,
  );
  return data;
}

export async function setLearnedRuleEnabled(
  ruleId: string,
  enabled: boolean,
): Promise<LearnedRule> {
  const { data } = await apiClient.patch<LearnedRule>(
    `/admin/learned-rules/${ruleId}`,
    { enabled },
  );
  return data;
}

export async function deleteLearnedRule(ruleId: string, confirm = false): Promise<void> {
  await apiClient.delete(`/admin/learned-rules/${ruleId}`, {
    params: confirm ? { confirm: true } : {},
  });
}

/* ---------- Test-run harness ---------- */

export interface SingleTestRunResult {
  rule_version_id: string;
  case_id: string;
  status: 'triggered' | 'not_verifiable' | 'clean' | 'error';
  evidence: string;
  citation: string;
  confidence: number;
  keyed_on: string[];
  error: string | null;
}

export async function testRunVersion(
  versionId: string,
  caseId: string,
): Promise<SingleTestRunResult> {
  const { data } = await apiClient.post<SingleTestRunResult>(
    `/admin/learned-rules/versions/${versionId}/test-run`,
    { case_id: caseId },
  );
  return data;
}

export interface TestCaseRecord {
  id: string;
  rule_version_id: string;
  case_id: string;
  expected: 'should_trigger' | 'should_not_trigger';
}

export async function recordTestCase(
  versionId: string,
  caseId: string,
  expected: 'should_trigger' | 'should_not_trigger',
): Promise<TestCaseRecord> {
  const { data } = await apiClient.post<TestCaseRecord>(
    `/admin/learned-rules/versions/${versionId}/test-cases`,
    { case_id: caseId, expected },
  );
  return data;
}

export interface SuiteCaseResult {
  case_id: string;
  expected: string;
  actual: string;
  outcome: 'TP' | 'FP' | 'TN' | 'FN' | 'indeterminate';
  evidence: string;
  error: string | null;
}

export interface SuiteVersionResult {
  rule_version_id: string;
  rule_code: string;
  version_no: number;
  tp: number;
  fp: number;
  tn: number;
  fn: number;
  indeterminate: number;
  precision: number | null;
  recall: number | null;
  cases: SuiteCaseResult[];
}

export async function runSuite(
  ruleVersionIds?: string[],
): Promise<SuiteVersionResult[]> {
  const body = ruleVersionIds && ruleVersionIds.length
    ? { rule_version_ids: ruleVersionIds }
    : {};
  const { data } = await apiClient.post<{ items: SuiteVersionResult[] }>(
    '/admin/learned-rules/run-suite',
    body,
  );
  return data.items;
}

/* ---------- generate draft candidates from feedback ---------- */

export interface CandidateDraft {
  rule_id: string;
  rule_code: string;
  version_id: string;
  version_no: number;
  deficiency_description: string;
  target_data: TargetData;
  severity: Severity;
  points: number;
  status: 'draft' | 'active' | 'retired';
  precision: number | null;
  recall: number | null;
  tp: number;
  fp: number;
  tn: number;
  fn: number;
  example_case_ids: string[];
  error: string | null;
}

export async function generateFromFeedback(
  scope?: { since?: string; case_ids?: string[] },
): Promise<CandidateDraft[]> {
  const body = scope && (scope.since || (scope.case_ids && scope.case_ids.length))
    ? scope
    : {};
  const { data } = await apiClient.post<{ items: CandidateDraft[] }>(
    '/admin/learned-rules/generate-from-feedback',
    body,
  );
  return data.items;
}